import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, AlertCircle } from 'lucide-react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { userApi } from '../../services/adminApi';

interface UserAddEditProps {
  userId?: string;
  onNavigate: (page: string) => void;
}

export const UserAddEdit: React.FC<UserAddEditProps> = ({ userId, onNavigate }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    is_admin: false,
    is_active: true,
    can_access_square: false,
    can_access_ai: false,
  });

  useEffect(() => {
    if (userId) {
      fetchUser();
    }
  }, [userId]);

  const fetchUser = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const response = await userApi.get(userId);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch user');
      }
      
      setFormData({
        email: response.data.email || '',
        full_name: response.data.full_name || '',
        is_admin: response.data.is_admin || false,
        is_active: response.data.is_active !== false,
        can_access_square: response.data.can_access_square || false,
        can_access_ai: response.data.can_access_ai || false,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      
      let response;
      if (userId) {
        response = await userApi.update(userId, formData);
      } else {
        response = await userApi.create(formData);
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to save user');
      }
      
      onNavigate('admin-users');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => onNavigate('admin-users')}
            variant="outline"
            icon={ArrowLeft}
            size="sm"
          >
            Back to Users
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">
            {userId ? 'Edit User' : 'Add User'}
          </h1>
        </div>
      </div>

      {error && <ErrorMessage message={error} />}

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={!!userId} // Can't change email of existing user
            />
            {userId && (
              <p className="mt-1 text-sm text-gray-500">
                Email cannot be changed for existing users
              </p>
            )}
          </div>

          <div>
            <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              type="text"
              id="full_name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700">Permissions</h3>
            
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="text-sm text-gray-700">
                Active User
              </label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_admin"
                checked={formData.is_admin}
                onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_admin" className="text-sm text-gray-700">
                Administrator
              </label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="can_access_square"
                checked={formData.can_access_square}
                onChange={(e) => setFormData({ ...formData, can_access_square: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="can_access_square" className="text-sm text-gray-700">
                Can Access Square
              </label>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="can_access_ai"
                checked={formData.can_access_ai}
                onChange={(e) => setFormData({ ...formData, can_access_ai: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="can_access_ai" className="text-sm text-gray-700">
                Can Access AI
              </label>
            </div>
          </div>

          {!userId && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Note about user creation:</p>
                  <p className="mt-1">
                    Users typically authenticate via Google OAuth. Creating a user manually
                    will allow them to be managed in the system, but they'll need to sign in
                    with Google using the email address you specify here.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              type="submit"
              variant="primary"
              icon={Save}
              loading={saving}
            >
              {userId ? 'Update User' : 'Create User'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onNavigate('admin-users')}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};